import React, { FocusEvent } from "react";

import { SetValueInterface } from "commons/types/form";

import {
    BtnBold,
    BtnBulletList,
    BtnClearFormatting,
    BtnItalic,
    BtnLink,
    BtnNumberedList,
    BtnRedo,
    BtnUnderline,
    BtnUndo,
    ContentEditableEvent,
    Editor,
    EditorProvider,
    Toolbar,
} from "react-simple-wysiwyg";

type PropsType = {
    readonly label: string;
    readonly value: string;
    readonly path: string;
    readonly hideToolbar?: boolean;
    readonly bigDefault?: boolean;
    readonly required?: boolean;
    readonly setValue: SetValueInterface;
    readonly checkErrorVisibility?: (path: string) => Array<string>;
    readonly onFocus?: (event: FocusEvent<HTMLElement>) => void;
    readonly onBlur?: (event: FocusEvent<HTMLElement>) => void;
};

function WysiwygSimpleEditor({
    label,
    value,
    path,
    hideToolbar = false,
    bigDefault,
    required,
    setValue,
    checkErrorVisibility,
    onFocus,
    onBlur,
}: PropsType) {
    const [focused, setFocused] = React.useState<boolean>(false);

    const onChangeAction = React.useCallback(
        (event: ContentEditableEvent) => setValue(path, event.target.value),
        [setValue, path],
    );

    const onFocusAction = React.useCallback(
        (event: FocusEvent<HTMLElement>) => {
            setFocused(true);

            if (onFocus) {
                onFocus(event);
            }
        },
        [onFocus],
    );

    const onBlurAction = React.useCallback(
        (event: FocusEvent<HTMLElement>) => {
            setFocused(false);

            if (onBlur) {
                onBlur(event);
            }
        },
        [onBlur],
    );

    const errors = React.useMemo(
        () => (checkErrorVisibility ? checkErrorVisibility(path) : []),
        [checkErrorVisibility, path],
    );

    return (
        <div
            className={`input ${focused ? "focused" : ""} wysiwyg-editor ${
                bigDefault ? "large-box" : ""
            } ${errors && errors.length > 0 ? " has-error " : ""}`}
        >
            <label>
                {label}
                {required ? (
                    <span aria-describedby={"obligatory-fields-info-description"}>*</span>
                ) : null}
            </label>

            <EditorProvider>
                <Editor
                    value={value}
                    onChange={onChangeAction}
                    onFocus={onFocusAction}
                    onBlur={onBlurAction}
                >
                    {!hideToolbar && (
                        <Toolbar>
                            <BtnUndo />
                            <BtnRedo />
                            <BtnBold />
                            <BtnItalic />
                            <BtnUnderline />
                            <BtnBulletList />
                            <BtnNumberedList />
                            <BtnLink />
                            <BtnClearFormatting />
                        </Toolbar>
                    )}
                </Editor>
            </EditorProvider>

            <span className={"errors"}>{errors ? errors.join(", ") : ""}</span>
        </div>
    );
}

export default WysiwygSimpleEditor;
